import Icon from "$store/components/ui/Icon.tsx";
import Button from "$store/components/ui/Button.tsx";
import QuantitySelector from "$store/components/ui/QuantitySelector.tsx";
import Image from "apps/website/components/Image.tsx";
import { useCart } from "apps/vtex/hooks/useCart.ts";
import { formatPrice } from "$store/sdk/format.ts";
import { sendEvent } from "$store/sdk/analytics.tsx";
import { useSignal } from "@preact/signals";
import { useCallback } from "preact/hooks";
import { toAnalytics } from "$store/sdk/ga4/transform/toAnalytics.ts";

interface Props {
  index: number;
  locale: string;
  currency: string;
}

function CartItem({ index, locale, currency }: Props) {
  const {
    cart,
    updateItems,
  } = useCart();
  const loading = useSignal(false);
  const item = cart.value!.items[index];
  const {
    imageUrl,
    skuName,
    sellingPrice,
    listPrice,
    name,
    quantity,
    detailUrl,
    id,
  } = item;

  const isGift = sellingPrice < 0.01;

  const withLoading = useCallback(
    <A,>(cb: (args: A) => void) => async (e: A) => {
      try {
        loading.value = true;
        await cb(e);
      } finally {
        loading.value = false;
      }
    },
    [loading],
  );

  return (
    <li class="py-4 mr-2 flex justify-between items-center gap-4 first:pt-0 last:pb-0">
      <a class="shrink-0" href={`${detailUrl}?skuId=${id}`}>
        <Image
          class="w-18 h-18 border border-tw-neutral-100 rounded object-cover object-center lg:w-20 lg:h-20"
          src={imageUrl.replace("-55-55", "")}
          alt={skuName}
          width={80}
          height={80}
          loading="lazy"
          fetchPriority="low"
        />
      </a>

      <div class="h-full flex-grow grid content-between gap-2">
        <a class="leading-none" href={`${detailUrl}?skuId=${id}`}>
          <span class="text-tw-neutral-500 text-xs font-bold">{name}</span>
        </a>

        <div class="flex flex-col gap-1.5 lg:flex-row lg:justify-between lg:items-end">
          <div class="whitespace-nowrap flex items-center gap-3 lg:flex-col lg:items-start lg:gap-0">
            {listPrice && listPrice > sellingPrice && (
              <span class="text-tw-neutral-400 text-xs line-through">
                De: {formatPrice(listPrice / 100, currency, locale)}
              </span>
            )}

            <span class="text-tw-secondary-200 leading-5 font-extrabold">
              {isGift
                ? "Grátis"
                : `${listPrice ? "Por: " : ""}${
                  formatPrice(sellingPrice / 100, currency, locale)
                }`}
            </span>
          </div>

          <div class="max-w-[6rem]">
            <QuantitySelector
              disabled={loading.value || isGift}
              quantity={quantity}
              onChange={withLoading(async (quantity) => {
                await updateItems({ orderItems: [{ index, quantity }] });

                if (!cart.value) return;

                const diff = quantity - item.quantity;

                const analytics = {
                  items: [{
                    sku: item.id,
                    inProductGroupWithID: item.productId,
                    name: item.name,
                    seller: item.seller,
                    listPrice: parseFloat((item.listPrice / 100).toFixed(2)),
                    price: parseFloat((item.price / 100).toFixed(2)),
                    url: `${self?.location.host}${item.detailUrl}`,
                    brand: item.additionalInfo.brandName ?? "",
                    category: Object.values(item.productCategories).join(">") ??
                      "",
                    extended: { index, quantity: Math.abs(diff) },
                  }],
                  view: { id: "minicart", name: "Carrinho" },
                };

                const removeFromCartEvent = toAnalytics({
                  type: "remove_from_cart",
                  data: analytics,
                });

                const addToCartEvent = toAnalytics({
                  type: "add_to_cart",
                  data: analytics,
                });

                sendEvent(diff < 0 ? removeFromCartEvent : addToCartEvent);
              })}
            />
          </div>
        </div>
      </div>

      <Button
        class="w-6 h-6 flex justify-center items-center"
        onClick={withLoading(async () => {
          await updateItems({ orderItems: [{ index, quantity: 0 }] });

          if (!cart.value) return;

          const analytics = {
            items: [{
              sku: item.id,
              inProductGroupWithID: item.productId,
              name: item.name,
              seller: item.seller,
              listPrice: parseFloat((item.listPrice / 100).toFixed(2)),
              price: parseFloat((item.price / 100).toFixed(2)),
              url: `${self?.location.host}${item.detailUrl}`,
              brand: item.additionalInfo.brandName ?? "",
              category: Object.values(item.productCategories).join(">") ?? "",
              extended: { index, quantity: item.quantity },
            }],
            view: { id: "minicart", name: "Carrinho" },
          };

          const removeFromCartEvent = toAnalytics({
            type: "remove_from_cart",
            data: analytics,
          });

          sendEvent(removeFromCartEvent);
        })}
        disabled={loading.value || isGift}
        loading={loading.value}
      >
        <Icon id="Trash" class="text-tw-neutral-200" width={15} height={16} />
      </Button>
    </li>
  );
}

export default CartItem;
